import BillingGate from "@/components/BillingGate";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isRestricted, scopedPages, hasRole } from "@/lib/roles";
import SupportFooter from "@/components/SupportFooter";
import Sidebar from "@/components/Sidebar";
import { Menu, X, ShieldAlert, LogOut } from "lucide-react";
import api from "@/lib/api";
import { ChatProvider } from "@/context/ChatContext";
import CallManager from "@/components/chat/CallManager";

function ImpersonationBanner() {
    const raw = typeof window !== "undefined" ? localStorage.getItem("hrm_impersonation") : null;
    if (!raw) return null;
    let info;
    try { info = JSON.parse(raw); } catch { return null; }
    const onReturn = async () => {
        try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
        localStorage.removeItem("hrm_token");
        localStorage.removeItem("hrm_impersonation");
        window.location.href = "/super-admin/login";
    };
    return (
        <div className="bg-amber-500 text-ink border-b-2 border-amber-600 px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap" data-testid="impersonation-banner">
            <div className="flex items-center gap-2.5 min-w-0">
                <ShieldAlert className="w-4 h-4 shrink-0" strokeWidth={2.2} />
                <div className="text-xs sm:text-sm">
                    <span className="font-bold uppercase tracking-[0.15em] text-[11px] mr-2">Impersonating</span>
                    <span className="truncate">
                        You are signed in as an admin of <span className="font-semibold">{info.company_name || info.company_id}</span> via super-admin <span className="font-mono text-[11px] bg-ink/10 px-1 py-0.5 rounded">{info.by}</span>
                    </span>
                </div>
            </div>
            <button
                onClick={onReturn}
                className="inline-flex items-center gap-1.5 bg-ink text-white px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-medium hover:bg-ink/80 rounded shrink-0"
                data-testid="impersonation-return-btn"
            >
                <LogOut className="w-3 h-3" /> Return to super-admin
            </button>
        </div>
    );
}

// HR Clerk / Appraiser only reach their own pages; anything else bounces to their first page.
function ScopeGuard({ children }) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    if (!isRestricted(user)) return children;
    const pages = scopedPages(user);
    const ok = pages.some((p) => pathname === p || pathname.startsWith(`${p}/`)) || pathname === "/face-enroll";
    return ok ? children : <Navigate to={pages[0]} replace />;
}

export default function AppLayout() {
    const { user } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [company, setCompany] = useState({ name: "", logo_data_url: "" });
    const location = useLocation();

    useEffect(() => {
        api.get("/company-settings")
            .then(({ data }) => setCompany({ name: data?.name || "", logo_data_url: data?.logo_data_url || "" }))
            .catch(() => {});
    }, []);

    // Close drawer on route change
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    // Lock body scroll while mobile drawer is open
    useEffect(() => {
        if (drawerOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [drawerOpen]);

    return (
        <ChatProvider>
        <CallManager />
        <div className="flex min-h-screen bg-background text-foreground" data-testid="app-layout">
            {/* Desktop sidebar */}
            <div className="hidden lg:block">
                <Sidebar />
            </div>

            {/* Mobile drawer */}
            {drawerOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm animate-fade-in"
                    onClick={() => setDrawerOpen(false)}
                    data-testid="mobile-drawer-overlay"
                >
                    <div
                        className="absolute inset-y-0 left-0 w-72 max-w-[85vw] shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Sidebar onNavigate={() => setDrawerOpen(false)} />
                        <button
                            onClick={() => setDrawerOpen(false)}
                            aria-label="Close menu"
                            className="absolute top-4 right-4 p-2 rounded-lg text-white/80 hover:text-white bg-white/10"
                            data-testid="mobile-drawer-close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            <main className={`flex-1 min-w-0 ${hasRole(user, "admin") && location.pathname !== "/chat" ? "pb-11" : ""}`}>
                <ImpersonationBanner />
                {/* Mobile top bar */}
                <div
                    className="lg:hidden sticky top-0 z-40 bg-card/95 backdrop-blur border-b border-border flex items-center justify-between px-4 py-3"
                    data-testid="mobile-topbar"
                >
                    <button
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open menu"
                        className="p-2 -ml-2 text-ink hover:bg-ink/5"
                        data-testid="mobile-menu-button"
                    >
                        <Menu className="w-6 h-6" strokeWidth={1.5} />
                    </button>
                    <div className="flex items-center gap-2 min-w-0">
                        {company.logo_data_url ? (
                            <img src={company.logo_data_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-[#ffffff] p-0.5" />
                        ) : (
                            <div className="w-8 h-8 rounded-lg bg-klein flex items-center justify-center">
                                <span className="font-display font-extrabold text-white text-sm">{(company.name || "H")[0].toUpperCase()}</span>
                            </div>
                        )}
                        <div className="font-display font-bold text-ink truncate max-w-[200px]" data-testid="mobile-company-name">{company.name || "HR/M"}</div>
                    </div>
                    <div className="w-6" />
                </div>

                <BillingGate><ScopeGuard><Outlet /></ScopeGuard></BillingGate>
                {hasRole(user, "admin") && location.pathname !== "/chat" && <SupportFooter />}
            </main>
        </div>
        </ChatProvider>
    );
}
